import { ChangeEvent, useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExcelData } from "@/lib/types";
import { useToast } from "@/components/ui/use-toast";
import { uploadExcelFile } from "@/lib/api";
import { Progress } from "@/components/ui/progress";

interface FileUploadProps {
  onFileLoaded: (data: ExcelData) => void;
}

const FileUpload = ({ onFileLoaded }: FileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const { toast } = useToast();

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length) {
      console.log("File dropped:", files[0].name);
      processFile(files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      console.log("File selected:", e.target.files[0].name);
      processFile(e.target.files[0]);
    }
  };

  const processFile = async (file: File) => {
    console.log("Starting file processing:", file.name);
    
    if (!file.name.endsWith('.xlsx')) {
      console.error("Invalid file format:", file.name);
      toast({
        title: "Invalid file format",
        description: "Please upload an Excel (.xlsx) file",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setProgress(0);
    setStatus("Starting file upload...");
    console.log("Upload status: Starting file upload...");

    try {
      setStatus("Reading Excel file...");
      console.log("Upload status: Reading Excel file...");
      
      const response = await uploadExcelFile(file);
      console.log("Upload response received:", response);
      
      // Create ExcelData object from the response
      const excelData: ExcelData = {
        fileName: file.name,
        sheets: response.sheets.map(sheet => ({
          name: sheet,
          headers: ["Model", "MOP", "Type", "Cashback", "Finance Options"],
          rows: [[`Processed ${response.total_rows} rows in ${response.processing_time}`]],
        })),
      };
      
      console.log("Created ExcelData:", excelData);
      setProgress(100);
      setStatus("Processing complete!");
      console.log("Upload status: Processing complete!");
      
      onFileLoaded(excelData);
      toast({
        title: "File uploaded successfully",
        description: `${response.message} (${response.processing_time})`,
      });
    } catch (error) {
      console.error("Error processing file:", error);
      setStatus("Error processing file");
      toast({
        title: "Error processing file",
        description: error instanceof Error ? error.message : "There was an error reading the Excel file",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      console.log("File processing completed");
    }
  };

  return (
    <div className="mb-8">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center ${
          isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
        } transition-colors duration-200 cursor-pointer`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById("file-input")?.click()}
      >
        <input
          id="file-input"
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={handleFileChange}
          disabled={isLoading}
        />
        <div className="flex flex-col items-center">
          <Upload size={40} className="text-gray-400 mb-3" />
          <p className="text-lg font-medium mb-1">
            {isLoading ? "Processing..." : "Upload Excel File"}
          </p>
          <p className="text-sm text-gray-500 mb-3">
            Drag and drop your .xlsx file or click to browse
          </p>
          <Button disabled={isLoading}>
            {isLoading ? "Processing..." : "Select File"}
          </Button>
          
          {isLoading && (
            <div className="w-full mt-4">
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-gray-500 mt-2">
                {status}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                This may take several minutes depending on the file size. Please check the browser console (F12) for detailed progress.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
